import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { FieldLabel } from '../../shared/ui/field-label';
import { Hint } from '../../shared/ui/hint';
import { Modal } from '../../shared/ui/modal';
import { Note } from '../../shared/ui/note';
import { Select } from '../../shared/ui/select';
import { ErrorState, LoadingState } from '../../shared/ui/states';
import { holdersWords, pinsForVersion, pinTitle, type IPin } from '../../entities/transform';
import {
  diffLibraryVersions,
  libraryKeys,
  rollbackLibraryVersion,
  type TLibraryItem,
  type TLibraryVersionSummary,
} from '../../entities/library';
import { VersionDiff } from './version-diff';

interface IProps {
  item: TLibraryItem;
  versions: TLibraryVersionSummary[];
  pins: IPin[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const LibraryCompare = ({ item, versions, pins, open, onOpenChange }: IProps) => {
  const numbers = versions.map((version) => version.version);
  const [to, setTo] = useState(numbers[0] ?? 1);
  const [from, setFrom] = useState(numbers[1] ?? numbers[0] ?? 1);
  const client = useQueryClient();
  const what = item.kind === 'prompt' ? 'messages' : 'code';

  const diff = useQuery({
    queryKey: libraryKeys.diff(item.id, from, to),
    queryFn: () => diffLibraryVersions(item.id, from, to),
    enabled: open && from !== to,
  });

  const rollback = useMutation({
    mutationFn: () => rollbackLibraryVersion(item.id, from, `Rollback to v${from}`),
    onSuccess: () => void client.invalidateQueries({ queryKey: libraryKeys.item(item.id) }),
  });

  const options = versions.map((version) => ({
    value: String(version.version),
    label: `v${version.version}`,
    description: version.comment ?? undefined,
  }));

  const held = pinsForVersion(pins, to);

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Comparing two versions"
      width="lg"
      footerNote={diff.data ? `+${diff.data.additions} · −${diff.data.deletions}` : undefined}
      submit={{
        label: `Start a new version from v${from}`,
        onSubmit: () => rollback.mutate(),
        disabled: from === to,
        waiting: rollback.isPending,
        waitingLabel: 'Writing it',
      }}
    >
      <div className="max-h-body overflow-auto">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <FieldLabel>From</FieldLabel>
            <Select
              value={String(from)}
              onChange={(next) => setFrom(Number(next))}
              options={options}
              aria-label="From version"
            />
          </div>
          <div>
            <FieldLabel>To</FieldLabel>
            <Select
              value={String(to)}
              onChange={(next) => setTo(Number(next))}
              options={options}
              aria-label="To version"
            />
          </div>
        </div>

        <div className="mt-4">
          {from === to && <Hint>Pick two different versions to see what moved between them</Hint>}
          {diff.isError && <ErrorState error={diff.error} onRetry={() => void diff.refetch()} />}
          {from !== to && diff.isPending && <LoadingState rows={6} />}
          {diff.data && <VersionDiff diff={diff.data} what={what} />}
        </div>

        {rollback.isError && <ErrorState error={rollback.error} />}

        {rollback.data && (
          <Note className="mt-4">
            v{rollback.data.version} now exists, carrying v{from}&rsquo;s {what}. Nothing was
            repointed at it: every step still names the version it named a moment ago.
          </Note>
        )}

        <Hint>
          Going back makes v{item.latestVersion + 1} with v{from}&rsquo;s {what} rather than moving
          v{to} out of the way.{' '}
          {held.length > 0
            ? `${holdersWords(held)} ${held.length === 1 ? 'pins' : 'pin'} v${to} right now (${held.map(pinTitle).join(', ')}), and keep running it until their steps say otherwise`
            : `Nothing is pinned to v${to} right now, so nothing changes either way until a step names one of these`}
        </Hint>
      </div>
    </Modal>
  );
};
